import type { ReactNode } from "react";
import { X } from "lucide-react";

// Shared bottom-sheet shell: ONE rounded card per sheet. The header row owns
// the single title and the close button; the body scrolls underneath it.
export interface FlowSheetShellProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly testId: string;
  // z-40 for the logging-flow overlay, z-50 for sheets mounted inside screens.
  readonly zLayer?: "z-40" | "z-50";
}

export function FlowSheetShell({ title, onClose, children, testId, zLayer = "z-40" }: FlowSheetShellProps) {
  return (
    <>
      <div
        data-testid={`${testId}-scrim`}
        className={`fixed inset-0 ${zLayer} bg-black/50`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`pointer-events-none fixed inset-x-0 bottom-0 ${zLayer} flex max-h-[90vh] flex-col justify-end p-4 pb-[max(env(safe-area-inset-bottom),1rem)]`}>
        <div
          data-testid={testId}
          className="pointer-events-auto mx-auto w-full max-w-xl overflow-y-auto rounded-2xl bg-surface ob-sheet-up"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-b border-border bg-surface px-5 py-2">
            <h3 className="text-lg font-semibold text-text">{title}</h3>
            <button
              type="button"
              data-testid={`${testId}-close`}
              aria-label="Close flow"
              className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
              onClick={onClose}
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>
          <div className="px-5 py-4">{children}</div>
        </div>
      </div>
    </>
  );
}
